import type { MythicPlusSummary } from '@moussenaires/shared';
import { useTranslation } from 'react-i18next';

import './CharacterMythicPlusPanel.css';

/**
 * The score (shown only here now — the modal's tab-agnostic stats row
 * dropped it once it became redundant with this panel), a world/region/realm
 * rank row with whichever is numerically best (lowest) called out, then the
 * best key per dungeon this season (one list, not split by spec — see the
 * plan notes on why), then the 10 most recent keys as the main "well
 * stylised" piece: each one colored by whether it was timed, and linking
 * straight to its Raider.IO run.
 */
export function CharacterMythicPlusPanel({
  mythicPlus,
  mythicPlusError,
}: {
  mythicPlus: MythicPlusSummary | null;
  mythicPlusError?: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  if (!mythicPlus) {
    return (
      <p className="character-enrichment-unavailable">
        {mythicPlusError ?? t('roster.notConfigured')}
      </p>
    );
  }

  const rankEntries = mythicPlus.ranks
    ? ([
        ['realm', mythicPlus.ranks.realm, t('roster.rankRealm')],
        ['region', mythicPlus.ranks.region, t('roster.rankRegion')],
        ['world', mythicPlus.ranks.world, t('roster.rankWorld')],
      ] as const)
    : null;
  const knownRanks = (rankEntries ?? [])
    .map(([, rank]) => rank)
    .filter((rank): rank is number => rank !== null);
  const bestRank = knownRanks.length > 0 ? Math.min(...knownRanks) : null;

  return (
    <div className="character-mythic-plus-panel">
      {mythicPlus.score !== null && (
        <p className="mythic-plus-score">
          <strong>{Math.round(mythicPlus.score)}</strong>{' '}
          <small>{t('roster.mythicPlusScore')}</small>
        </p>
      )}

      {rankEntries && (
        <div className="mythic-plus-ranks">
          {rankEntries.map(([key, rank, label]) => (
            <div
              key={key}
              className={`mythic-plus-rank ${rank !== null && rank === bestRank ? 'mythic-plus-rank-best' : ''}`}
            >
              <strong>{rank !== null ? `#${rank.toLocaleString(locale)}` : '—'}</strong>
              <span>{label}</span>
            </div>
          ))}
        </div>
      )}

      <h3 className="character-detail-section-title">{t('roster.bestRuns')}</h3>
      {mythicPlus.bestRuns.length > 0 ? (
        <div className="mythic-plus-best-table-wrap">
          <table className="mythic-plus-best-table">
            <thead>
              <tr>
                <th>{t('roster.dungeon')}</th>
                <th>{t('roster.bestKeyLevel')}</th>
                <th>{t('roster.mythicPlusScore')}</th>
              </tr>
            </thead>
            <tbody>
              {mythicPlus.bestRuns.map((run) => (
                <tr key={run.dungeon}>
                  <td>
                    <a href={run.url} target="_blank" rel="noreferrer">
                      {run.shortName ?? run.dungeon}
                    </a>
                  </td>
                  <td>+{run.level}</td>
                  <td>{run.score ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="character-enrichment-unavailable">{t('common.empty')}</p>
      )}

      <h3 className="character-detail-section-title">{t('roster.recentRuns')}</h3>
      {mythicPlus.recentRuns.length > 0 ? (
        <ul className="mythic-plus-recent-runs">
          {mythicPlus.recentRuns.map((run, index) => (
            <li
              key={`${run.dungeon}-${run.level}-${index}`}
              className={`mythic-plus-recent-run ${
                run.inTime === true
                  ? 'mythic-plus-recent-run-in'
                  : run.inTime === false
                    ? 'mythic-plus-recent-run-out'
                    : ''
              }`}
            >
              <a href={run.url} target="_blank" rel="noreferrer">
                <span className="mythic-plus-recent-run-dungeon">
                  {run.shortName ?? run.dungeon}
                </span>
                <span className="mythic-plus-recent-run-level">+{run.level}</span>
                {run.score !== null && (
                  <span className="mythic-plus-recent-run-score">{run.score}</span>
                )}
                <span className="mythic-plus-recent-run-status">
                  {run.inTime === true
                    ? t('roster.inTime')
                    : run.inTime === false
                      ? t('roster.notInTime')
                      : ''}
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="character-enrichment-unavailable">{t('common.empty')}</p>
      )}
    </div>
  );
}
